import { createTheme, alpha } from "@mui/material/styles";

// ── Brand + semantic color constants ─────────────────────────────────────────
export const BRAND = {
  cyan:   "#33C5E8",
  yellow: "#F5D547",
  coral:  "#EF6B6B",
  purple: "#A78BFA",
  green:  "#4ADE80",
} as const;

// 12%-opacity dim backgrounds — used for badges / chips
export const DIM = {
  cyan:   "rgba(51,197,232,0.12)",
  yellow: "rgba(245,213,71,0.12)",
  coral:  "rgba(239,107,107,0.12)",
  purple: "rgba(167,139,250,0.12)",
  green:  "rgba(74,222,128,0.12)",
} as const;

// ── Chart color sequence ──────────────────────────────────────────────────────
export const CHART_COLORS = [
  BRAND.cyan,
  BRAND.yellow,
  "#1AB0D6",    // cyan deep
  BRAND.green,
  BRAND.purple,
  "#FFE57F",    // pale yellow
];

// ── Per-mode raw tokens ───────────────────────────────────────────────────────
const light = {
  bgBase:      "#F6F7F9",
  bgSurface:   "#FFFFFF",
  bgCard:      "#FFFFFF",
  bgCardHover: "#F0F2F5",
  bgElevated:  "#F0F2F5",

  border:       "rgba(0,0,0,0.08)",
  borderMedium: "rgba(0,0,0,0.12)",
  borderStrong: "rgba(0,0,0,0.18)",

  textPrimary:   "#111318",
  textSecondary: "#5A6070",
  textTertiary:  "#8B93A7",

  cyanDim:   "rgba(51,197,232,0.10)",
  yellowDim: "rgba(210,175,20,0.10)",
};

const dark = {
  bgBase:      "#0B0E14",
  bgSurface:   "#12161F",
  bgCard:      "#171C28",
  bgCardHover: "#1C2233",
  bgElevated:  "#1F2537",

  border:       "rgba(255,255,255,0.06)",
  borderMedium: "rgba(255,255,255,0.10)",
  borderStrong: "rgba(255,255,255,0.15)",

  textPrimary:   "#E8ECF4",
  textSecondary: "#8B93A7",
  textTertiary:  "#545D72",

  cyanDim:   "rgba(51,197,232,0.12)",
  yellowDim: "rgba(245,213,71,0.12)",
};

// ── Theme factory ─────────────────────────────────────────────────────────────
export function createAppTheme(mode: "light" | "dark") {
  const t = mode === "dark" ? dark : light;

  const cardShadow  = mode === "dark"
    ? `0 1px 4px rgba(0,0,0,0.30), 0 0 0 1px ${t.border}`
    : `0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px ${t.border}`;
  const hoverShadow = mode === "dark"
    ? `0 4px 16px rgba(0,0,0,0.40), 0 0 0 1px ${t.borderMedium}`
    : `0 4px 12px rgba(0,0,0,0.08), 0 0 0 1px ${t.borderMedium}`;
  const modalShadow = mode === "dark"
    ? `0 8px 40px rgba(0,0,0,0.60)`
    : `0 8px 32px rgba(0,0,0,0.12)`;

  return createTheme({
    palette: {
      mode,
      primary: {
        main:        BRAND.cyan,
        dark:        "#1AB0D6",
        light:       t.cyanDim,
        contrastText: "#0B0E14",
      },
      secondary: {
        main:        BRAND.yellow,
        light:       t.yellowDim,
        contrastText: "#0B0E14",
      },
      background: {
        default: t.bgBase,
        paper:   t.bgCard,
      },
      text: {
        primary:   t.textPrimary,
        secondary: t.textSecondary,
        disabled:  t.textTertiary,
      },
      divider: t.border,
      success: { main: BRAND.green,  light: DIM.green  },
      warning: { main: BRAND.yellow, light: DIM.yellow  },
      error:   { main: BRAND.coral,  light: DIM.coral   },
      info:    { main: BRAND.cyan,   light: DIM.cyan    },
    },

    shape: { borderRadius: 6 },

    typography: {
      fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
      // Display scale — Outfit
      h1: { fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 600, lineHeight: 1.25, color: t.textPrimary },
      h2: { fontFamily: "'Outfit', sans-serif", fontSize: 24, fontWeight: 600, lineHeight: 1.3,  color: t.textPrimary },
      h3: { fontFamily: "'Outfit', sans-serif", fontSize: 20, fontWeight: 600, lineHeight: 1.35, color: t.textPrimary },
      h4: { fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 600, lineHeight: 1.4,  color: t.textPrimary },
      // Body scale — DM Sans
      h5: { fontSize: 15, fontWeight: 600, lineHeight: 1.45, color: t.textPrimary },
      h6: { fontSize: 13, fontWeight: 600, lineHeight: 1.5,  color: t.textPrimary },
      body1:   { fontSize: 15, fontWeight: 400, lineHeight: 1.6  },
      body2:   { fontSize: 13, fontWeight: 400, lineHeight: 1.55 },
      caption: { fontSize: 12, fontWeight: 500, color: t.textSecondary },
      overline: {
        fontSize: 11, fontWeight: 600, letterSpacing: "0.8px",
        textTransform: "uppercase", color: t.textSecondary,
      },
      button: { fontSize: 13, fontWeight: 600, textTransform: "none", letterSpacing: 0 },
    },

    shadows: [
      "none",
      cardShadow,
      hoverShadow,
      modalShadow,
      ...Array(21).fill(modalShadow),
    ] as any,

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: t.bgBase },
          "*": { boxSizing: "border-box" },
          // mono utility class
          ".mono": { fontFamily: "'JetBrains Mono', monospace", fontSize: 12 },
        },
      },

      // ── Card ───────────────────────────────────────────────────────────
      MuiCard: {
        defaultProps: { elevation: 1 },
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundColor: t.bgCard,
            boxShadow: cardShadow,
            backgroundImage: "none",
            transition: "box-shadow 150ms ease",
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: "16px 20px",
            "&:last-child": { paddingBottom: 16 },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            backgroundImage: "none",
            backgroundColor: t.bgCard,
          },
          elevation1: { boxShadow: cardShadow },
          elevation2: { boxShadow: hoverShadow },
          elevation3: { boxShadow: modalShadow },
        },
      },

      // ── Button ─────────────────────────────────────────────────────────
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 4,
            padding: "6px 16px",
            fontSize: 13,
            fontWeight: 600,
            transition: "all 150ms ease",
          },
          contained: {
            backgroundColor: BRAND.cyan,
            color: "#0B0E14",
            "&:hover": { backgroundColor: "#1AB0D6", boxShadow: "none" },
          },
          outlined: {
            borderColor: t.borderMedium,
            color: t.textPrimary,
            "&:hover": {
              borderColor: BRAND.cyan,
              backgroundColor: t.cyanDim,
            },
          },
          text: {
            color: t.textSecondary,
            "&:hover": { backgroundColor: t.bgElevated, color: t.textPrimary },
          },
          sizeSmall: { padding: "4px 10px", fontSize: 12 },
          sizeLarge: { padding: "8px 22px", fontSize: 14 },
        },
      },

      // ── IconButton ─────────────────────────────────────────────────────
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            color: t.textSecondary,
            "&:hover": { backgroundColor: t.bgElevated, color: t.textPrimary },
          },
        },
      },

      // ── Chip / Badge ───────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 4, fontSize: 11, fontWeight: 600, height: 22 },
          colorPrimary: { backgroundColor: DIM.cyan,   color: BRAND.cyan   },
          colorSecondary: { backgroundColor: DIM.yellow, color: "#B89A00"  },
          colorSuccess: { backgroundColor: DIM.green,  color: BRAND.green  },
          colorWarning: { backgroundColor: DIM.yellow, color: "#B89A00"    },
          colorError:   { backgroundColor: DIM.coral,  color: BRAND.coral  },
          colorInfo:    { backgroundColor: DIM.purple, color: BRAND.purple },
        },
      },

      // ── TextField / Input ──────────────────────────────────────────────
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontSize: 14,
            backgroundColor: t.bgSurface,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: t.borderMedium },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.borderStrong },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: BRAND.cyan,
              borderWidth: 1.5,
            },
          },
          input: { color: t.textPrimary },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: 14, color: t.textSecondary },
        },
      },
      MuiSelect: {
        styleOverrides: { root: { borderRadius: 4 } },
      },

      // ── Menu ───────────────────────────────────────────────────────────
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 6,
            boxShadow: hoverShadow,
            border: `1px solid ${t.borderMedium}`,
            backgroundColor: t.bgElevated,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 13,
            borderRadius: 4,
            margin: "2px 6px",
            padding: "6px 10px",
            color: t.textSecondary,
            "&:hover": { backgroundColor: t.bgCardHover, color: t.textPrimary },
            "&.Mui-selected": { backgroundColor: t.cyanDim, color: BRAND.cyan },
          },
        },
      },

      // ── List / Sidebar nav ─────────────────────────────────────────────
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 500,
            color: t.textSecondary,
            padding: "7px 10px",
            "&:hover": { backgroundColor: t.bgCardHover, color: t.textPrimary },
            "&.Mui-selected": {
              backgroundColor: "transparent",
              color: t.textPrimary,
              fontWeight: 600,
              borderLeft: `2px solid ${BRAND.cyan}`,
              paddingLeft: 8,
              "&:hover": { backgroundColor: t.bgCardHover },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { minWidth: 32, color: "inherit" },
        },
      },

      // ── Dialog / Modal ─────────────────────────────────────────────────
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            boxShadow: modalShadow,
            backgroundColor: t.bgCard,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: "'Outfit', sans-serif",
            fontSize: 18,
            fontWeight: 600,
            paddingBottom: 8,
            color: t.textPrimary,
          },
        },
      },

      // ── Table ──────────────────────────────────────────────────────────
      MuiTableHead: {
        styleOverrides: {
          root: { backgroundColor: t.bgBase },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.6px",
            color: t.textSecondary,
            borderBottom: `1px solid ${t.border}`,
            padding: "10px 16px",
            backgroundColor: t.bgBase,
          },
          body: {
            fontSize: 14,
            color: t.textPrimary,
            borderBottom: `1px solid ${t.border}`,
            padding: "12px 16px",
          },
        },
      },

      // ── Tooltip ────────────────────────────────────────────────────────
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12,
            fontWeight: 500,
            backgroundColor: mode === "dark" ? t.bgElevated : "#1C2233",
            color: "#E8ECF4",
            borderRadius: 4,
            padding: "5px 10px",
            border: `1px solid ${t.borderMedium}`,
          },
        },
      },

      // ── Divider ────────────────────────────────────────────────────────
      MuiDivider: {
        styleOverrides: { root: { borderColor: t.border } },
      },

      // ── Tabs ───────────────────────────────────────────────────────────
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2, borderRadius: 2, backgroundColor: BRAND.cyan },
          root: { borderBottom: `1px solid ${t.border}` },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            textTransform: "none",
            color: t.textSecondary,
            minHeight: 44,
            padding: "8px 16px",
            "&.Mui-selected": { color: BRAND.cyan, fontWeight: 600 },
          },
        },
      },

      // ── Alert ──────────────────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 6, fontSize: 13 },
          standardSuccess: { backgroundColor: DIM.green,  color: BRAND.green  },
          standardWarning: { backgroundColor: DIM.yellow, color: "#B89A00"    },
          standardError:   { backgroundColor: DIM.coral,  color: BRAND.coral  },
          standardInfo:    { backgroundColor: DIM.cyan,   color: BRAND.cyan   },
        },
      },

      // ── LinearProgress ─────────────────────────────────────────────────
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 4, height: 4, backgroundColor: t.bgElevated },
          bar:  { borderRadius: 4, backgroundColor: BRAND.cyan },
        },
      },

      // ── Skeleton ───────────────────────────────────────────────────────
      MuiSkeleton: {
        styleOverrides: {
          root: { borderRadius: 4, backgroundColor: t.bgElevated },
        },
      },

      // ── Accordion ──────────────────────────────────────────────────────
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: t.bgCard,
            backgroundImage: "none",
            boxShadow: cardShadow,
            borderRadius: "6px !important",
            "&:before": { display: "none" },
          },
        },
      },

      // ── Switch ─────────────────────────────────────────────────────────
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            "&.Mui-checked": { color: BRAND.cyan },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: BRAND.cyan,
              opacity: 0.4,
            },
          },
        },
      },
    },
  });
}

// ── Default export (light) — for non-context consumers ───────────────────────
export const theme = createAppTheme("light");

// ── Expose light tokens for inline sx props ───────────────────────────────────
export const palette = light;
export { light as lightTokens, dark as darkTokens };
